/**
 * EveryMatrix Partner API — panel tarafının ortak parçaları.
 *
 * Sağlayıcının iki yüzeyi vardır ve panel ikisini birden kullanır: oyun ve
 * para akışı (`/v1/*`, bizde `/admin/emsports/...`) ile self-servis yönetim
 * (`/partner/v1/*`, bizde `/admin/emsports/partner/...`). Yollar backend'de
 * sabittir; buradaki sarmalayıcılar yalnızca panelin tekrar tekrar aynı
 * adresi yazmasını önler.
 *
 * İki kural bu dosyanın tamamını belirler:
 *
 * - **Sağlayıcının reddi olduğu gibi gösterilir.** Yönetim uçlarının hatası
 *   sabit bir koda bağlıdır (`partner_api_disabled`, `invalid_signature`,
 *   `422 invalid_risk`) ve tek bir "işlem başarısız" cümlesine indirgenirse
 *   yönetici olmayan bir anahtar arızasını aramaya başlar.
 * - **`ignored_fields` sessiz kalmaz.** Dokümanın kendi uyarısı: tanınmayan
 *   bir alan uygulanmaz ve cevapta bildirilir — okunmazsa yönetici
 *   korunduğunu sanır.
 */
import client from '../../lib/api';

const BASE = '/admin/emsports';
const PARTNER = `${BASE}/partner`;

/** Sağlayıcı/HTTP hatasının okunabilir metni. */
export const errorText = (error, fallback = 'İstek başarısız') => (
  error?.response?.data?.detail || error?.message || fallback
);

/**
 * Yazma cevabındaki "uygulanmadı" listesi.
 *
 * Sağlayıcı tanımadığı alanı **sessizce** atar ve adını `ignored_fields`
 * içinde döndürür; panel bunu yazmazsa yazım hatası olan bir limit kaydedildi
 * sanılır ve kurulum korumasız kalır.
 */
export const ignoredFields = (data) => {
  const rows = data?.ignored_fields;
  return Array.isArray(rows) ? rows.filter(Boolean).map(String) : [];
};

/** JSON metnini nesneye çevirir; hatayı okunur bir cümleye dönüştürür. */
export const parseJson = (text, fallback = {}) => {
  const raw = String(text || '').trim();
  if (!raw) return { value: fallback, error: '' };
  try {
    const value = JSON.parse(raw);
    if (value === null || typeof value !== 'object') {
      return { value: null, error: 'Gövde bir JSON nesnesi olmalıdır' };
    }
    return { value, error: '' };
  } catch (error) {
    return { value: null, error: `Geçersiz JSON: ${error.message}` };
  }
};

/** Nesneyi düzenlenebilir metne çevirir (iki boşluk girinti). */
export const toJsonText = (value) => {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

export const partnerApi = {
  config: () => client.get(`${PARTNER}/config`),
  summary: () => client.get(`${PARTNER}/summary`),
  bets: (params) => client.get(`${PARTNER}/bets`, { params }),
  bet: (betId) => client.get(`${PARTNER}/bets/${encodeURIComponent(betId)}`),
  retryCredit: (betId) => client.post(
    `${PARTNER}/bets/${encodeURIComponent(betId)}/retry-credit`),
  alerts: (limit = 100) => client.get(`${PARTNER}/alerts`, {
    params: { limit },
  }),
  flags: () => client.get(`${PARTNER}/flags`),
  catalog: () => client.get(`${PARTNER}/catalog`),
  markets: (sport) => client.get(`${PARTNER}/markets`, { params: { sport } }),

  saveSettings: (body) => client.patch(`${PARTNER}/settings`, body),
  saveVisibility: (body) => client.put(`${PARTNER}/visibility`, body),
  saveCashout: (body) => client.put(`${PARTNER}/cashout`, body),
  saveRiskConfig: (body) => client.put(`${PARTNER}/risk-config`, body),
  riskRules: () => client.get(`${PARTNER}/risk-rules`),
  saveRiskRule: (body) => client.post(`${PARTNER}/risk-rules`, body),
  deleteRiskRule: (id) => client.delete(
    `${PARTNER}/risk-rules/${encodeURIComponent(id)}`),
  riskAudit: () => client.get(`${PARTNER}/risk-audit`),

  themePresets: () => client.get(`${PARTNER}/theme-presets`),
  theme: () => client.get(`${PARTNER}/theme`),
  saveTheme: (body) => client.patch(`${PARTNER}/theme`, body),

  tradingLive: (sport) => client.get(`${PARTNER}/trading/live`, {
    params: sport ? { sport } : undefined,
  }),
  tradingMarkets: (matchId) => client.get(
    `${PARTNER}/trading/markets/${encodeURIComponent(matchId)}`),
  overrides: () => client.get(`${PARTNER}/trading/overrides`),
  saveOverride: (body) => client.post(`${PARTNER}/trading/overrides`, body),
  deleteOverride: (id) => client.delete(
    `${PARTNER}/trading/overrides/${encodeURIComponent(id)}`),

  brands: () => client.get(`${PARTNER}/brands`),
  createBrand: (body) => client.post(`${PARTNER}/brands`, body),
  saveBrand: (id, body) => client.patch(
    `${PARTNER}/brands/${encodeURIComponent(id)}`, body),
  deleteBrand: (id) => client.delete(
    `${PARTNER}/brands/${encodeURIComponent(id)}`),

  webhooks: () => client.get(`${PARTNER}/webhooks`),
  saveWebhooks: (body) => client.patch(`${PARTNER}/webhooks`, body),
  webhookQueue: () => client.get(`${PARTNER}/webhooks/queue`),
  alertChannel: () => client.get(`${PARTNER}/alerts-channel`),
  saveAlertChannel: (body) => client.patch(`${PARTNER}/alerts-channel`, body),

  rotateKey: () => client.post(`${PARTNER}/key-rotate`),
};

export const appearanceApi = {
  themes: () => client.get(`${BASE}/themes`),
  banners: () => client.get(`${BASE}/banners`),
  createBanner: (body) => client.post(`${BASE}/banners`, body),
  saveBanner: (id, body) => client.patch(
    `${BASE}/banners/${encodeURIComponent(id)}`, body),
  deleteBanner: (id) => client.delete(
    `${BASE}/banners/${encodeURIComponent(id)}`),
  cms: () => client.get(`${BASE}/cms`),
  createBlock: (body) => client.post(`${BASE}/cms`, body),
  saveBlock: (id, body) => client.post(
    `${BASE}/cms/${encodeURIComponent(id)}`, body),
  deleteBlock: (id) => client.delete(
    `${BASE}/cms/${encodeURIComponent(id)}`),
};

/**
 * Ödemenin durumu — sağlayıcının kümesi.
 *
 * `dead` yönetici için **eylem gerektiren** tek değerdir: sağlayıcı on sekiz
 * denemeden sonra vazgeçer ve ödeme kendiliğinden bir daha çıkmaz.
 */
export const CREDIT_STATUS_LABELS = {
  none: 'Ödeme yok',
  pending: 'Kuyrukta',
  failed: 'Başarısız',
  done: 'Ödendi',
  dead: 'Ölü — elle kuyruğa alın',
};

export const CREDIT_STATUS_TONE = {
  none: 'text-slate-400',
  pending: 'text-sky-300',
  failed: 'text-amber-300',
  done: 'text-emerald-300',
  dead: 'text-rose-400',
};

/** Ödeme yeniden kuyruğa alınabilir mi (yalnızca başarısız/ölü). */
export const canRetryCredit = (status) => (
  status === 'failed' || status === 'dead'
);
